/**
 * /portal/team — Admin: invite members (email or link), list pending invites, manage team.
 * Read-only view for members.
 */
import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UserPlus, Mail, Link as LinkIcon, Copy, Trash2, Send, ShieldCheck, User as UserIcon, CheckCircle2, XCircle, Coins, Plus } from 'lucide-react';
import { usePortalStore, toast } from '../../stores';
import { onboardingApi } from '../../services/onboarding';
import { portalTeamAllocApi } from '../../services/api';
import { NexusButton } from '../../components/nexus-ui/NexusButton';
import { NexusInput } from '../../components/nexus-ui/NexusInput';
import { NexusCard } from '../../components/nexus-ui/NexusCard';

const PortalTeam = () => {
  const me = usePortalStore((s) => s.user);
  const isAdmin = me?.role === 'admin' || !!me?.is_super_admin;
  const [team, setTeam] = useState([]);
  const [invites, setInvites] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [mode, setMode] = useState('email');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [sending, setSending] = useState(false);
  // Per-user credit allocation state (admin only).
  const [alloc, setAlloc] = useState({ pool_balance: 0, members: [] });
  const [allocOpenFor, setAllocOpenFor] = useState(null);
  const [allocAmount, setAllocAmount] = useState('');
  const allocByUser = useMemo(
    () => Object.fromEntries((alloc.members || []).map((m) => [m.user_id, m])),
    [alloc],
  );

  const load = async () => {
    try {
      const t = await onboardingApi.listTeam();
      setTeam(t.data.items || []);
    } catch {}
    if (isAdmin) {
      try {
        const i = await onboardingApi.listInvites();
        setInvites(i.data.items || []);
      } catch {}
      try {
        const { data } = await portalTeamAllocApi.list();
        setAlloc(data);
      } catch {}
    }
  };
  useEffect(() => { load(); }, [isAdmin]);

  const sendInvite = async (e) => {
    e?.preventDefault?.();
    if (mode === 'email' && !email) { toast.error('Enter an email address'); return; }
    setSending(true);
    try {
      const { data } = await onboardingApi.createInvite({ email: mode === 'email' ? email : null, role, mode });
      if (mode === 'email') {
        toast.success(data.email_sent ? `Invite emailed to ${email}` : `Invite created — copy the link below to share`);
      } else {
        await navigator.clipboard.writeText(data.activation_link).catch(() => {});
        toast.success('Invite link copied to clipboard');
      }
      setEmail(''); setShowInvite(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create invite');
    } finally { setSending(false); }
  };

  const copyLink = async (link) => {
    try { await navigator.clipboard.writeText(link); toast.success('Link copied'); }
    catch { toast.error('Could not copy — select and copy manually'); }
  };

  const revoke = async (id) => {
    if (!confirm('Revoke this invite?')) return;
    try { await onboardingApi.revokeInvite(id); toast.success('Invite revoked'); await load(); }
    catch { toast.error('Could not revoke'); }
  };

  const resend = async (id) => {
    try { const { data } = await onboardingApi.resendInvite(id); toast.success(data.ok ? 'Resent' : 'Sent'); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not resend'); }
  };

  const updateRole = async (id, newRole) => {
    try { await onboardingApi.updateMember(id, { role: newRole }); toast.success('Role updated'); await load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not update'); }
  };

  const remove = async (id, email) => {
    if (!confirm(`Remove ${email} from the organization?`)) return;
    try { await onboardingApi.removeMember(id); toast.success('Member removed'); await load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not remove'); }
  };

  return (
    <div className="space-y-6" data-testid="portal-team">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-semibold tracking-wider uppercase" style={{ color: '#F26B1F' }}>ORGANIZATION</div>
          <h1 className="font-heading text-3xl font-semibold text-nexus-text-primary mt-1">Team</h1>
          <p className="text-[13px] text-nexus-text-secondary mt-1">
            {team.length} {team.length === 1 ? 'member' : 'members'}
            {isAdmin && invites.length > 0 && <> · <span className="text-nexus-accent-orange">{invites.length} pending invite{invites.length > 1 ? 's' : ''}</span></>}
          </p>
        </div>
        {isAdmin && (
          <NexusButton onClick={() => setShowInvite(true)} data-testid="invite-member-btn">
            <UserPlus size={14} /> Invite member
          </NexusButton>
        )}
      </div>

      {/* Invite modal */}
      <AnimatePresence>
        {showInvite && isAdmin && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.5)' }}
            onClick={() => setShowInvite(false)}>
            <motion.form onSubmit={sendInvite}
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-2xl p-6"
              style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}>
              <h3 className="font-heading text-xl font-semibold text-nexus-text-primary">Invite someone</h3>
              <p className="text-[12px] text-nexus-text-secondary mt-1">Pick how you'd like to send the invite.</p>

              <div className="mt-4 grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setMode('email')}
                  className={`p-3 rounded-lg text-left transition-colors ${mode === 'email' ? 'ring-2' : ''}`}
                  style={{ background: 'var(--layer-2)', border: '1px solid var(--hairline)',
                           boxShadow: mode === 'email' ? '0 0 0 2px #F26B1F' : 'none' }}
                  data-testid="invite-mode-email">
                  <Mail size={14} style={{ color: '#F26B1F' }} />
                  <div className="text-[13px] font-semibold text-nexus-text-primary mt-1.5">Email</div>
                  <div className="text-[11px] text-nexus-text-tertiary">We send them a link</div>
                </button>
                <button type="button" onClick={() => setMode('link')}
                  className={`p-3 rounded-lg text-left transition-colors ${mode === 'link' ? 'ring-2' : ''}`}
                  style={{ background: 'var(--layer-2)', border: '1px solid var(--hairline)',
                           boxShadow: mode === 'link' ? '0 0 0 2px #F26B1F' : 'none' }}
                  data-testid="invite-mode-link">
                  <LinkIcon size={14} style={{ color: '#6BA6F5' }} />
                  <div className="text-[13px] font-semibold text-nexus-text-primary mt-1.5">Shareable link</div>
                  <div className="text-[11px] text-nexus-text-tertiary">Copy &amp; paste yourself</div>
                </button>
              </div>

              <div className="mt-4 space-y-3">
                {mode === 'email' && (
                  <NexusInput label="Their email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="colleague@company.com" icon={<Mail size={14} />} required data-testid="invite-email" />
                )}
                <div>
                  <label className="block text-[11px] uppercase tracking-wider font-semibold mb-1.5 text-nexus-text-tertiary">Role</label>
                  <select value={role} onChange={(e) => setRole(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-lg text-[13px] outline-none"
                    style={{ background: 'var(--layer-2)', border: '1px solid var(--hairline)', color: 'var(--nexus-text-primary)' }}
                    data-testid="invite-role">
                    <option value="member">Member — chat + own usage</option>
                    <option value="admin">Admin — manage team, buy credits, billing</option>
                  </select>
                </div>
              </div>

              <div className="mt-6 flex gap-2 justify-end">
                <button type="button" onClick={() => setShowInvite(false)}
                  className="px-4 py-2 rounded-lg text-[12px] font-semibold text-nexus-text-secondary hover:bg-nexus-bg-tertiary"
                  data-testid="invite-cancel">Cancel</button>
                <NexusButton type="submit" isLoading={sending} data-testid="invite-send">
                  <Send size={12} /> {mode === 'email' ? 'Send invite' : 'Generate link'}
                </NexusButton>
              </div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Pending invites */}
      {isAdmin && invites.length > 0 && (
        <div className="rounded-xl p-5" style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}>
          <div className="text-[10px] font-semibold tracking-wider uppercase mb-3" style={{ color: '#F26B1F' }}>PENDING INVITES</div>
          <div className="space-y-1.5">
            {invites.map((inv) => (
              <div key={inv.id} className="flex items-center gap-3 py-2 border-b last:border-0"
                style={{ borderColor: 'var(--hairline)' }}
                data-testid={`pending-invite-${inv.id}`}>
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: inv.mode === 'email' ? 'rgba(242,107,31,0.1)' : 'rgba(107,166,245,0.1)' }}>
                  {inv.mode === 'email' ? <Mail size={12} style={{ color: '#F26B1F' }} /> : <LinkIcon size={12} style={{ color: '#6BA6F5' }} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-nexus-text-primary truncate">
                    {inv.email || <i className="text-nexus-text-tertiary">Anyone with the link</i>}
                  </div>
                  <div className="text-[11px] text-nexus-text-tertiary truncate">
                    {inv.role} · {inv.expired ? <span className="text-red-400">Expired</span> : `Expires ${new Date(inv.expires_at).toLocaleDateString()}`}
                  </div>
                </div>
                <button onClick={() => copyLink(inv.activation_link)}
                  className="text-[11px] px-2 py-1 rounded-md text-nexus-text-secondary hover:bg-nexus-bg-tertiary"
                  title="Copy link" data-testid={`copy-link-${inv.id}`}>
                  <Copy size={12} />
                </button>
                {inv.mode === 'email' && (
                  <button onClick={() => resend(inv.id)}
                    className="text-[11px] px-2 py-1 rounded-md text-nexus-text-secondary hover:bg-nexus-bg-tertiary"
                    title="Resend" data-testid={`resend-${inv.id}`}>
                    <Send size={12} />
                  </button>
                )}
                <button onClick={() => revoke(inv.id)}
                  className="text-[11px] px-2 py-1 rounded-md hover:bg-nexus-bg-tertiary"
                  style={{ color: '#EF4444' }} title="Revoke" data-testid={`revoke-${inv.id}`}>
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Members */}
      <div className="rounded-xl p-5" style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}>
        <div className="text-[10px] font-semibold tracking-wider uppercase mb-3" style={{ color: '#F26B1F' }}>MEMBERS</div>
        <div className="space-y-1">
          {team.map((u) => (
            <div key={u.id} className="flex items-center gap-3 py-2.5 border-b last:border-0"
              style={{ borderColor: 'var(--hairline)' }}
              data-testid={`member-${u.id}`}>
              <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: 'var(--layer-2)', border: '1px solid var(--hairline)' }}>
                <span className="text-[13px] font-semibold text-nexus-text-primary">
                  {(u.name || u.email || '?').charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-semibold text-nexus-text-primary">
                    {u.name || u.email}
                  </span>
                  {u.id === me?.id && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: 'rgba(107,166,245,0.15)', color: '#6BA6F5' }}>You</span>}
                </div>
                <div className="text-[11px] text-nexus-text-tertiary truncate">{u.email}</div>
              </div>
              {isAdmin && u.id !== me?.id ? (
                <select value={u.role || 'member'} onChange={(e) => updateRole(u.id, e.target.value)}
                  className="text-[11px] px-2 py-1 rounded-md text-nexus-text-primary"
                  style={{ background: 'var(--layer-2)', border: '1px solid var(--hairline)' }}
                  data-testid={`role-${u.id}`}>
                  <option value="member">member</option>
                  <option value="admin">admin</option>
                </select>
              ) : (
                <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md text-nexus-text-secondary"
                  style={{ background: 'var(--layer-2)' }}>
                  {u.role === 'admin' ? <ShieldCheck size={11} style={{ color: '#F26B1F' }} /> : <UserIcon size={11} />}
                  {u.role || 'member'}
                </span>
              )}
              {isAdmin && allocByUser[u.id] && (
                <div className="hidden md:flex items-center gap-3 px-3 py-1 rounded-md min-w-[180px]"
                     style={{ background: 'var(--layer-2)', border: '1px solid var(--hairline)' }}
                     title={`Lifetime allocated: ${allocByUser[u.id].lifetime_allocated} · Lifetime consumed: ${allocByUser[u.id].lifetime_consumed}`}
                     data-testid={`alloc-${u.id}`}>
                  <Coins size={11} className="text-nexus-accent-mint" />
                  <span className="text-[11px] font-mono text-nexus-text-primary">
                    {Number(allocByUser[u.id].personal_balance).toLocaleString()}
                  </span>
                  <span className="text-[9px] uppercase tracking-wider text-nexus-text-tertiary">credits</span>
                </div>
              )}
              {isAdmin && (
                <button
                  onClick={() => { setAllocOpenFor(u.id); setAllocAmount(''); }}
                  className="text-[11px] px-2 py-1 rounded-md inline-flex items-center gap-1"
                  style={{ background: 'rgba(16,185,129,0.12)', color: '#10B981', border: '1px solid rgba(16,185,129,0.3)' }}
                  title="Top up this member's credit allocation"
                  data-testid={`topup-${u.id}`}
                >
                  <Plus size={11} /> Top up
                </button>
              )}
              {isAdmin && u.id !== me?.id && (
                <button onClick={() => remove(u.id, u.email)}
                  className="text-[11px] p-1 rounded-md hover:bg-nexus-bg-tertiary"
                  style={{ color: '#EF4444' }} title="Remove"
                  data-testid={`remove-${u.id}`}>
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Org-pool balance summary — admins only */}
      {isAdmin && (
        <div className="rounded-xl p-4 flex items-center justify-between"
             style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                 style={{ background: 'rgba(16,185,129,0.15)', color: '#10B981' }}>
              <Coins size={18} />
            </div>
            <div>
              <div className="text-[10px] font-semibold tracking-wider uppercase" style={{ color: '#F26B1F' }}>Org pool</div>
              <div className="font-heading text-xl font-semibold text-nexus-text-primary">
                {Number(alloc.pool_balance || 0).toLocaleString()} <span className="text-[10px] uppercase text-nexus-text-tertiary">credits available</span>
              </div>
            </div>
          </div>
          <div className="text-[11px] text-nexus-text-tertiary text-right">
            Members spend from their personal allocation only — top up via the buttons above.<br/>
            Lifetime granted: <strong>{Number(alloc.pool_lifetime_granted || 0).toLocaleString()}</strong> · purchased: <strong>{Number(alloc.pool_lifetime_purchased || 0).toLocaleString()}</strong>
          </div>
        </div>
      )}

      {/* Top-up modal */}
      <AnimatePresence>
        {allocOpenFor && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={() => setAllocOpenFor(null)}
            data-testid="alloc-modal"
          >
            <motion.div
              initial={{ scale: 0.95, y: 8 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 8 }}
              className="w-[420px] max-w-[92vw] rounded-2xl p-6 space-y-4"
              style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div>
                <div className="text-[10px] font-semibold tracking-wider uppercase" style={{ color: '#F26B1F' }}>Credit allocation</div>
                <h3 className="font-heading text-lg font-semibold text-nexus-text-primary mt-1">
                  Top up · {team.find(m => m.id === allocOpenFor)?.name || team.find(m => m.id === allocOpenFor)?.email}
                </h3>
                <p className="text-[12px] text-nexus-text-tertiary mt-1">
                  Current balance: <strong>{Number(allocByUser[allocOpenFor]?.personal_balance || 0).toLocaleString()}</strong> credits ·
                  Org pool: <strong>{Number(alloc.pool_balance).toLocaleString()}</strong> available
                </p>
              </div>
              <div>
                <div className="text-[10px] font-semibold tracking-wider uppercase text-nexus-text-tertiary mb-1.5">Credits to allocate</div>
                <input
                  type="number"
                  autoFocus
                  value={allocAmount}
                  onChange={(e) => setAllocAmount(e.target.value)}
                  placeholder="e.g., 5000"
                  className="w-full px-3 py-2 rounded-lg text-[13px]"
                  style={{ background: 'var(--layer-2)', border: '1px solid var(--hairline)', color: 'var(--nexus-text-primary)' }}
                  data-testid="alloc-amount"
                />
                <div className="text-[10px] text-nexus-text-tertiary mt-1">
                  Positive = grant from pool · Negative = recall back to pool
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t" style={{ borderColor: 'var(--hairline)' }}>
                <button
                  onClick={() => setAllocOpenFor(null)}
                  className="px-3 py-2 rounded-lg text-[12px] font-semibold"
                  style={{ background: 'var(--layer-2)', color: 'var(--nexus-text-secondary)' }}
                >Cancel</button>
                <button
                  onClick={async () => {
                    const amt = Number(allocAmount);
                    if (!amt) { toast.error('Enter a non-zero amount'); return; }
                    try {
                      await portalTeamAllocApi.allocate(allocOpenFor, amt);
                      toast.success(`Allocated ${amt > 0 ? '+' : ''}${amt} credits`);
                      setAllocOpenFor(null);
                      load();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Allocation failed');
                    }
                  }}
                  className="px-4 py-2 rounded-lg text-[12px] font-semibold text-white"
                  style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}
                  data-testid="alloc-submit"
                >
                  Apply
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PortalTeam;
